/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Calendar,
  Check,
  ChevronDown,
  Minus,
  Plus,
  Search,
  User,
  Plane,
  Wallet,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, FormControl } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

import {
  useTravelTypes,
  useBudgets,
  useSearchPlaces,
  useCreatePlan,
} from "../hooks";
import heroBg from "@/assets/image.png";
import Container from "@/components/ui/Container";
import { router } from "@/config/router";
import { RateLimitDialog } from "./RateLimitDialog";

/** Static trip types */
const TRIP_MODES = [
  { value: "solo", icon: "👤" },
  { value: "family", icon: "👨‍👩‍👧‍👦" },
  { value: "partner", icon: "💑" },
  { value: "friends", icon: "👥" },
];

// Simplistic store for plans logic ported from TravelHug
// In a real app, use Zustand or Context
// eslint-disable-next-line
export let plans: any = null;

export function setLastPlan(plan: any) {
  plans = plan;
}

export function getLastPlan() {
  if (plans) {
    return JSON.stringify(plans);
  }
  return null;
}

export function HeroPlanner() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        backgroundColor: "#FFF3EA",
        backgroundImage: `url(${heroBg})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center bottom",
        backgroundSize: "cover",
        minHeight: "600px",
      }}
    >
      <Container>
        <div className="relative z-10 py-10 sm:py-14 flex flex-col items-center">
          <PlannerForm />
        </div>
      </Container>
      <div className="h-16 sm:h-20" />
    </section>
  );
}

export function PlannerForm() {
  const { t, i18n } = useTranslation();
  // values
  const [tripMode, setTripMode] = useState("");
  const [people, setPeople] = useState(4);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  // meta
  const { data: travelTypesData } = useTravelTypes();
  const travelTypes = travelTypesData?.items || [];

  const { data: budgetsData } = useBudgets();
  const budgets = budgetsData?.items || [];

  const [budgetId, setBudgetId] = useState("");
  const navigate = useNavigate();

  // preference (multi)
  const [selectedPrefIds, setSelectedPrefIds] = useState<any[]>([]);
  const togglePref = (id: any) =>
    setSelectedPrefIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // place search
  const [query, setQuery] = useState("");
  // placeResults is now derived from query hook
  const [placesOpen, setPlacesOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  // debounceRef removed

  // validation messages
  const [errs, setErrs] = useState({
    place: "",
    start: "",
    end: "",
    tripMode: "",
    budget: "",
  });

  // submit state
  const { mutateAsync: createPlanMutate, isPending: submitting } = useCreatePlan();
  const [okMsg, setOkMsg] = useState("");
  const [errorOverlay, setErrorOverlay] = useState({
    show: false,
    message: "Something went wrong",
  });
  const [rateLimitDialog, setRateLimitDialog] = useState({
    show: false,
    message: "",
    resetAt: undefined as number | undefined,
  });
  const lastBodyRef = useRef<any>(null); // keep last request body for retry

  // today (yyyy-mm-dd) from LOCAL time (avoid UTC shift)
  const toLocalYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const todayStr = toLocalYMD(new Date());

  // Helper function to calculate day difference
  const getDayDifference = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // make end min = start (if set), otherwise today
  const endMin = start || todayStr;

  // Calculate max end date (10 days after start date)
  const endMax = start ? (() => {
    const startDate = new Date(start);
    startDate.setDate(startDate.getDate() + 10);
    return toLocalYMD(startDate);
  })() : undefined;

  // adjust people control when trip type changes
  useEffect(() => {
    if (tripMode === "solo") setPeople(1);
    else if (tripMode === "partner") setPeople(2);
    else if (people < 1) setPeople(1);
  }, [tripMode]); // eslint-disable-line

  // load meta
  // (Removed manual effect, using hooks)

  // debounced places
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    if (!query || selectedPlace) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, selectedPlace]);

  const { data: placeSearchResults, error: searchError } = useSearchPlaces(debouncedQuery, !!debouncedQuery);
  // Derived place results
  const placeResults = (placeSearchResults || []).slice(0, 10);

  // Handle search rate limit
  useEffect(() => {
    if (searchError) {
      const e = searchError as any;
      const status = e?.response?.status;
      const errorData = e?.response?.data?.error;

      if (status === 429 && errorData?.code === "RATE_LIMITED") {
        setRateLimitDialog({
          show: true,
          message: errorData.message || t('planner.rateLimit.searchLimit'),
          resetAt: errorData.details?.reset_at,
        });
        setPlacesOpen(false); // close dropdown
      }
    }
  }, [searchError]);

  // Auto-open when results change
  useEffect(() => {
    if (placeResults.length > 0) {
      setPlacesOpen(true);
    }
  }, [placeResults]); // eslint-disable-line

  // close suggestions on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!searchBoxRef.current) return;
      if (!searchBoxRef.current.contains(e.target as Node)) setPlacesOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // validate fields and return boolean
  const validate = () => {
    const next = { place: "", start: "", end: "", tripMode: "", budget: "" };

    if (!selectedPlace) next.place = t('planner.validation.selectDestination');
    if (!start) next.start = t('planner.validation.chooseStartDate');
    else if (start < todayStr) next.start = t('planner.validation.startDatePast');

    if (!end) next.end = t('planner.validation.chooseEndDate');
    else if (end < start) next.end = t('planner.validation.endDateBeforeStart');
    else if (start && getDayDifference(start, end) > 9) {
      next.end = t('planner.validation.maxTenDays', { defaultValue: 'Trip duration cannot exceed 10 days' });
    }

    if (!tripMode) next.tripMode = t('planner.validation.chooseTripType');
    if (!budgetId) next.budget = t('planner.validation.chooseBudget');

    setErrs(next);
    return Object.values(next).every((m) => !m);
  };

  // submit
  const onSubmit = async () => {
    setOkMsg("");
    if (!validate()) return;

    const budget = budgets.find((b) => String(b.id) === String(budgetId));

    const body = {
      travel_type: travelTypes.filter(tp => selectedPrefIds.includes(tp.id)).map(tp => tp.id).join(","),
      budget_type: (budget?.id || budget?.id || "")
        .toString()
        .toLowerCase(),
      person_count:
        tripMode === "solo" ? 1 : tripMode === "partner" ? 2 : people,
      start_date: start,
      end_date: end,
      destination: selectedPlace?.name || selectedPlace?.destination || "",
      latitude: selectedPlace?.latitude ?? selectedPlace?.lat ?? null,
      longitude: selectedPlace?.longitude ?? selectedPlace?.lng ?? null,
      country_code: selectedPlace?.country_code ?? null,
      language: i18n.resolvedLanguage
    };
    console.log("Submitting plan:", body);
    lastBodyRef.current = body; // save for retry
    await createPlan(body);
  };

  const createPlan = async (body: any) => {
    try {
      console.log({ body })
      setOkMsg(""); // Clear prev messages
      const response = await createPlanMutate(body);
      setOkMsg(t('planner.messages.planCreated'));

      setLastPlan(response);
      console.log("Created plan:", response);

      // Navigate to the plan detail page using the plan_id from response
      if (response.slug) {
        navigate({ to: `/plans/$planId`, params: { planId: response.slug } });
      }
    } catch (e: any) {
      console.log("Create plan error:", e);
      setOkMsg("");

      // Check if it's a 429 rate limit error
      const status = e?.response?.status;
      const errorData = e?.response?.data?.error;

      if (status === 429 && errorData?.code === "RATE_LIMITED") {
        // Show rate limit dialog
        setRateLimitDialog({
          show: true,
          message: errorData.message || t('planner.rateLimit.planLimit'),
          resetAt: errorData.details?.reset_at,
        });
      } else {
        // Show generic error overlay for other errors
        console.error(e?.response?.data || e.message);
        setErrorOverlay({
          show: true,
          message: t('planner.messages.somethingWrong'),
        });
      }
    }
  };

  // retry handler for the overlay
  const handleRetry = async () => {
    setErrorOverlay((s) => ({ ...s, show: false }));
    if (lastBodyRef.current) {
      await createPlan(lastBodyRef.current);
    }
  };

  // login handler for rate limit dialog
  const handleLogin = () => {
    // Navigate to login page - update this path when login page is created
    // For now, this can redirect to your auth provider or home page
    window.location.href = "/auth/login";
  };

  // UI helpers
  const stepperDisabled = tripMode === "solo" || tripMode === "partner";

  return (
    <>
      {/* Loader overlay shown WHILE creating plan */}
      <LoadingOverlay
        show={submitting}
        quoteTop={t('planner.loading.quoteTop')}
        quoteBottom={t('planner.loading.quoteBottom')}
        backgroundImage={heroBg}
      />

      {/* Error overlay with Try Again */}
      <ErrorOverlay
        show={errorOverlay.show}
        message={errorOverlay.message}
        backgroundImage={heroBg}
        onRetry={handleRetry}
        onClose={() => setErrorOverlay((s) => ({ ...s, show: false }))}
      />

      {/* Rate Limit Dialog */}
      <RateLimitDialog
        show={rateLimitDialog.show}
        message={rateLimitDialog.message}
        resetAt={rateLimitDialog.resetAt}
        backgroundImage={heroBg}
        onLogin={handleLogin}
        onClose={() => setRateLimitDialog((s) => ({ ...s, show: false }))}
      />

      <div className="w-full max-w-2xl">
        {/* Destination search */}
        <div className="relative" ref={searchBoxRef}>
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
            <Search size={18} />
          </div>
          <input
            value={selectedPlace ? (selectedPlace.name || selectedPlace.destination) : query}
            onChange={(e) => {
              setSelectedPlace(null);
              setQuery(e.target.value);
              if (errs.place) setErrs((s) => ({ ...s, place: "" }));
            }}
            onFocus={() => query && setPlacesOpen(true)}
            placeholder={t('planner.form.searchPlaceholder')}
            className={`w-full h-12 pl-10 pr-4 rounded-[10px] bg-white border ${errs.place ? "border-red-400" : "border-[#EDE8E3]"
              } placeholder:text-slate-400`}
          />
          {placesOpen && placeResults.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full bg-white border border-[#EDE8E3] rounded-[10px] max-h-64 overflow-auto shadow-sm">
              {placeResults.map((p, i) => (
                <li
                  key={`${p.id ?? p.name}-${i}`}
                  className="px-3 py-2 hover:bg-[#FFF3EA] cursor-pointer"
                  onClick={() => {
                    setSelectedPlace(p);
                    setQuery(p.name || p.destination || "");
                    setPlacesOpen(false);
                    if (errs.place) setErrs((s) => ({ ...s, place: "" }));

                    const payload = {
                      budget_type: "budget",
                      destination: p.destination || p.name,
                      end_date: "2025-10-12",
                      language: "english",
                      latitude: p.latitude ?? p.lat,
                      longitude: p.longitude ?? p.lng,
                      country_code: p.country_code,
                      person_count: 2,
                      start_date: "2025-10-10",
                      travel_type: "leisure",
                    };
                    setLastPlan(payload);
                  }}
                  onKeyDown={() => { }}
                >
                  {p.destination}
                </li>
              ))}
            </ul>
          )}
        </div>
        {errs.place && (
          <p className="text-red-600 text-xs mt-1">{errs.place}</p>
        )}

        <form
          className="mt-3 grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <MergedDateRange
            onStartChange={(v: any) => {
              setStart(v);
              if (errs.start) setErrs((s) => ({ ...s, start: "" }));
              // Re-validate end date when start changes
              if (end && end < v) {
                setErrs((s) => ({
                  ...s,
                  end: t('planner.validation.endDateBeforeStart'),
                }));
              } else if (end && v && getDayDifference(v, end) > 10) {
                setErrs((s) => ({
                  ...s,
                  end: t('planner.validation.maxTenDays', { defaultValue: 'Trip duration cannot exceed 10 days' }),
                }));
              } else if (errs.end) {
                setErrs((s) => ({ ...s, end: "" }));
              }
            }}
            onEndChange={(v: any) => {
              setEnd(v);
              if (start && v && v < start) {
                setErrs((s) => ({
                  ...s,
                  end: t('planner.validation.endDateBeforeStart'),
                }));
              } else if (start && v && getDayDifference(start, v) > 10) {
                setErrs((s) => ({
                  ...s,
                  end: t('planner.validation.maxTenDays', { defaultValue: 'Trip duration cannot exceed 10 days' }),
                }));
              } else if (errs.end) {
                setErrs((s) => ({ ...s, end: "" }));
              }
            }}
            start={start}
            end={end}
            startMin={todayStr}
            endMin={endMin}
            endMax={endMax}
            errStart={errs.start}
            errEnd={errs.end}
          />

          <div className="grid grid-cols-2 gap-3">
            {/* Trip Type */}
            <SimpleSelect
              value={tripMode}
              onChange={(v: any) => {
                setTripMode(v);
                if (errs.tripMode) setErrs((s) => ({ ...s, tripMode: "" }));
              }}
              placeholder={t('planner.form.tripType')}
              options={TRIP_MODES.map((m) => ({ value: m.value, label: t(`planner.tripTypes.${m.value}` as any), icon: m.icon }))}
              error={errs.tripMode}
            />

            {/* People stepper (disabled for solo/partner) */}
            <div>
              <div
                className={`w-full h-12 rounded-[10px] bg-white border border-[#EDE8E3] px-2 flex items-center justify-between ${stepperDisabled ? "opacity-60" : ""
                  }`}
              >
                <button
                  type="button"
                  className="w-8 h-8 grid place-items-center rounded-full border border-[#EDE8E3] bg-[#EFEFEF]"
                  onClick={() =>
                    !stepperDisabled && setPeople((n) => Math.max(1, n - 1))
                  }
                  disabled={stepperDisabled}
                  aria-disabled={stepperDisabled}
                >
                  <Minus size={16} />
                </button>
                <div className="flex items-center gap-2 text-slate-700">
                  <User size={18} className="text-slate-400" />
                  <span className="font-medium">
                    {tripMode === "solo"
                      ? 1
                      : tripMode === "partner"
                        ? 2
                        : people}{" "}
                    {t('planner.form.person')}
                  </span>
                </div>
                <button
                  type="button"
                  className="w-8 h-8 grid place-items-center rounded-full border border-[#EDE8E3] bg-[#EFEFEF]"
                  onClick={() => !stepperDisabled && setPeople((n) => n + 1)}
                  disabled={stepperDisabled}
                  aria-disabled={stepperDisabled}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Budget */}
            <SimpleSelect
              value={budgetId}
              onChange={(v: any) => {
                setBudgetId(v);
                if (errs.budget) setErrs((s) => ({ ...s, budget: "" }));
              }}
              placeholder={t('planner.form.budget')}
              options={budgets.map((b) => ({ value: b.id, label: b.label, icon: b.icon }))}
              error={errs.budget}
            />

            {/* Preference (optional multi) */}
            <PreferenceMulti
              items={travelTypes}
              value={selectedPrefIds}
              onChange={setSelectedPrefIds}
              placeholder={t('planner.form.preference')}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-[4px] bg-[#ED6F2E] hover:bg-[#e36522] disabled:opacity-70 text-white font-semibold mt-1"
          >
            {submitting ? t('planner.form.creating') : t('planner.form.createButton')}
          </button>

          {okMsg && <div className="text-green-700 text-sm mt-2">{okMsg}</div>}
        </form>
      </div>
    </>
  );
}

/* ---------- date range with inline errors ---------- */
function MergedDateRange({
  start,
  end,
  onStartChange,
  onEndChange,
  startMin,
  endMin,
  endMax,
  errStart,
  errEnd,
}: any) {
  const { t } = useTranslation();

  // Control open state for pickers
  const [openStart, setOpenStart] = useState(false);
  const [openEnd, setOpenEnd] = useState(false);

  // Convert string dates to Dayjs objects for MUI DatePicker
  const startDate = start ? dayjs(start) : null;
  const endDate = end ? dayjs(end) : null;
  const minStartDate = startMin ? dayjs(startMin) : dayjs();
  const minEndDate = endMin ? dayjs(endMin) : dayjs();
  const maxEndDate = endMax ? dayjs(endMax) : undefined;

  const handleStartChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      onStartChange(newValue.format('YYYY-MM-DD'));
    } else {
      onStartChange('');
    }
  };

  const handleEndChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      onEndChange(newValue.format('YYYY-MM-DD'));
    } else {
      onEndChange('');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div>
        <div className="grid grid-cols-2 gap-3">
          {/* Start Date Picker */}
          <div>
            <DatePicker
              value={startDate}
              onChange={handleStartChange}
              minDate={minStartDate}
              format="MMM DD, YYYY"
              open={openStart}
              onOpen={() => setOpenStart(true)}
              onClose={() => setOpenStart(false)}
              slotProps={{
                textField: {
                  onClick: () => setOpenStart(true),
                  placeholder: t('planner.form.startDate'),
                  error: !!errStart,
                  fullWidth: true,
                  size: "small",
                  InputProps: {
                    startAdornment: (
                      <Calendar size={18} className="text-[#F0723E] mr-2" />
                    ),
                  },
                  inputProps: {
                    readOnly: true, // Prevent keyboard on mobile
                    style: { cursor: 'pointer' }
                  },
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '10px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      '& fieldset': {
                        borderColor: errStart ? '#f87171' : '#EDE8E3',
                      },
                      '&:hover fieldset': {
                        borderColor: errStart ? '#f87171' : '#EDE8E3',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errStart ? '#f87171' : '#0F766E',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#0f172a',
                      cursor: 'pointer',
                    },
                  },
                },
              }}
            />
          </div>

          {/* End Date Picker */}
          <div>
            <DatePicker
              value={endDate}
              onChange={handleEndChange}
              minDate={minEndDate}
              maxDate={maxEndDate}
              format="MMM DD, YYYY"
              open={openEnd}
              onOpen={() => setOpenEnd(true)}
              onClose={() => setOpenEnd(false)}
              slotProps={{
                textField: {
                  onClick: () => setOpenEnd(true),
                  placeholder: t('planner.form.endDate'),
                  error: !!errEnd,
                  fullWidth: true,
                  size: "small",
                  InputProps: {
                    startAdornment: (
                      <Calendar size={18} className="text-[#F0723E] mr-2" />
                    ),
                  },
                  inputProps: {
                    readOnly: true, // Prevent keyboard on mobile
                    style: { cursor: 'pointer' }
                  },
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '10px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      '& fieldset': {
                        borderColor: errEnd ? '#f87171' : '#EDE8E3',
                      },
                      '&:hover fieldset': {
                        borderColor: errEnd ? '#f87171' : '#EDE8E3',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errEnd ? '#f87171' : '#0F766E',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#0f172a',
                      cursor: 'pointer',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {(errStart || errEnd) && (
          <div className="grid grid-cols-2 gap-3 mt-1">
            <p className="text-red-600 text-xs">{errStart}</p>
            <p className="text-red-600 text-xs">{errEnd}</p>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
}

/* ---------- simple select with error ---------- */
function SimpleSelect({
  value,
  onChange,
  placeholder,
  options,
  error,
}: any) {
  // Find the selected option to display its icon
  const selectedOption = options.find((opt: any) => opt.value === value);

  return (
    <div>
      <FormControl fullWidth size="small" error={!!error}>
        <Select
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          displayEmpty
          IconComponent={(props) => (
            <ChevronDown {...props} size={18} className="text-slate-400 mr-4" />
          )}
          renderValue={(selected) => {
            if (!selected) {
              return <span className="text-slate-400">{placeholder}</span>;
            }
            const option = options.find((opt: any) => opt.value === selected);
            return (
              <div className="flex items-center gap-2">
                {option?.icon && <span className="text-lg">{option.icon}</span>}
                <span>{option?.label}</span>
              </div>
            );
          }}
          sx={{
            height: '48px',
            borderRadius: '10px',
            backgroundColor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? '#f87171' : '#EDE8E3',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? '#f87171' : '#EDE8E3',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? '#f87171' : '#EDE8E3',
            },
            color: '#334155',
          }}
        >
          <MenuItem value="" disabled>
            <span className="text-slate-400">{placeholder}</span>
          </MenuItem>
          {options.map((opt: any) => (
            <MenuItem key={opt.value} value={opt.value}>
              <div className="flex items-center gap-2">
                {opt.icon && <span className="text-lg">{opt.icon}</span>}
                <span>{opt.label}</span>
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
}

/* ---------- preference multi (optional) ---------- */
function PreferenceMulti({ items, value, onChange, placeholder }: any) {
  const { t } = useTranslation();
  const selected = items.filter((it: any) => value.includes(it.id));
  const count = selected.length;
  const labelText = count ? t('planner.form.selectedCount', { count }) : placeholder;

  return (
    <div>
      <FormControl fullWidth size="small">
        <Select
          multiple
          value={value || []}
          onChange={(e) => onChange && onChange(e.target.value)}
          displayEmpty
          IconComponent={(props) => (
            <ChevronDown {...props} size={18} className="text-slate-400 mr-4" />
          )}
          renderValue={(selected: any) => {
            if (!selected || selected.length === 0) {
              return <span className="text-slate-400">{placeholder}</span>;
            }
            const selectedItems = items.filter((it: any) => selected.includes(it.id));
            return (
              <div className="flex items-center gap-1">
                {selectedItems.slice(0, 2).map((item: any) => (
                  item.icon && <span key={item.id} className="text-lg">{item.icon}</span>
                ))}
                <span className="ml-1">
                  {t('planner.form.selectedCount', { count: selectedItems.length })}
                </span>
              </div>
            );
          }}
          sx={{
            height: '48px',
            borderRadius: '10px',
            backgroundColor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#EDE8E3',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#EDE8E3',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#EDE8E3',
            },
            color: '#334155',
          }}
        >
          {items.map((it: any) => {
            const isSelected = value.includes(it.id);
            return (
              <MenuItem key={it.id} value={it.id}>
                <div className="flex items-center gap-2 w-full">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className="mr-1"
                  />
                  {it.icon && <span className="text-lg">{it.icon}</span>}
                  <span className="flex-1">{it.label}</span>
                  {isSelected && <Check size={16} className="text-[#0F766E]" />}
                </div>
              </MenuItem>
            );
          })}
          {items.length === 0 && (
            <MenuItem disabled>
              <span className="text-slate-500">{t('planner.form.noOptions')}</span>
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {/* preference is optional → no error text */}
    </div>
  );
}

/* ---------- Loading Overlay (3-ball Newton's cradle) ---------- */
function LoadingOverlay({ show, quoteTop, quoteBottom, backgroundImage }: any) {
  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        backgroundImage: `linear-gradient(rgba(255,243,234,0.92), rgba(255,243,234,0.92)), url(${backgroundImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
      }}
    >
      {/* Animation styles */}
      <style>{`

        .ball{
            position: relative;
            transform-origin: 50% -100px;/* x axis y axis*/
        }

        .ball::before{
            content: '';
            position: absolute;
            width: 1px;
            height: 100px;
            background-color: white;
            top: -100px;
            left: 50%;
        }

        .ball--start{
            animation: start 0.8s infinite alternate;
        }
        .ball--end{
            animation: end 0.8s infinite 0.8s alternate;
        }
        @keyframes start {
            0%, 50%{
                transform: rotate(0);
            }
            100%{
                transform: rotate(30deg);
            }
        }
        @keyframes end {
            0%, 50%{
                transform: rotate(0);
            }
            100%{
                transform: rotate(-30deg);
            }
        }
      `}</style>

      <div className="flex flex-col items-center px-6 text-center">
        <div className="lan mb-2 w-[250px]">
          <div className="bar w-[100%] h-[10px] bg-amber-500 rounded-[8px]"></div>
          <div className="balls mt-[100px] flex">
            <div className="ball ball--start bg-amber-600 w-[50px] h-[50px] rounded-[50%]"></div>
            <div className="ball bg-amber-600 w-[50px] h-[50px] rounded-[50%]"></div>
            <div className="ball bg-amber-600 w-[50px] h-[50px] rounded-[50%]"></div>
            <div className="ball bg-amber-600 w-[50px] h-[50px] rounded-[50%]"></div>
            <div className="ball ball--end bg-amber-600 w-[50px] h-[50px] rounded-[50%]"></div>
          </div>
        </div>

        {/* Quote */}
        <p className="mt-6 text-slate-700 text-base sm:text-lg font-medium leading-snug">
          {quoteTop}
          <br />
          {quoteBottom}
        </p>
      </div>
    </div>
  );
}

/* ---------- Error Overlay with “Try Again” ---------- */
function ErrorOverlay({
  show,
  message,
  onRetry,
  onClose,
  backgroundImage,
}: any) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Request error"
      style={{
        backgroundImage: `linear-gradient(rgba(255,243,234,0.92), rgba(255,243,234,0.92)), url(${backgroundImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
      }}
    >
      <div className="bg-white/90 backdrop-blur-sm border border-[#F2E8E2] rounded-2xl shadow-lg w-[90%] max-w-md px-6 py-6 text-center">
        <h3 className="text-slate-800 font-extrabold text-xl">
          {t('planner.error.title')}
        </h3>
        <p className="mt-2 text-slate-600">{message}</p>
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            onClick={onRetry}
            className="h-11 px-5 rounded-[6px] bg-[#ED6F2E] hover:bg-[#e36522] text-white font-semibold"
          >
            {t('planner.messages.tryAgain')}
          </button>
          <button
            onClick={onClose}
            className="h-11 px-4 rounded-[6px] border border-[#E5DED8] text-slate-700 font-semibold bg-white"
          >
            {t('planner.error.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* util */
function capital(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
